import { Box, Divider, Paper, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../componentes/newAlertModal";
import FlowHeader from "../components/flowHeader";
import FlowTable from "../components/flowTable";
import { useTabStyles } from "../components/styles";
import ModalCdn from "./modalCdn";
import MassCdns from "./massCdns";
import { cdnColumns } from "./columns";

import { Cdn, CdnPayload } from "./types";
import api from "../../../../services/api";
import { AxiosError } from "axios";

export default function Cdns() {
  const [search, setSearch] = useState<string>("");
  const [cdns, setCdns] = useState<Cdn[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMassMode, setIsMassMode] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);

  const [openCdnModal, setOpenCdnModal] = useState<boolean>(false);
  const [editingCdn, setEditingCdn] = useState<Cdn | null>(null);
  const [savingCdn, setSavingCdn] = useState<boolean>(false);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedCdn, setSelectedCdn] = useState<Cdn | null>(null);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();

  useEffect(() => {
    getCdns();
  }, []);

  const getCdns = async () => {
    try {
      setLoading(true);

      const response = await api.get("/easyFlow2.0/cdn");

      setCdns(Array.isArray(response.data?.data) ? response.data.data : []);
      setTableKey((key) => key + 1);
    } catch (error: any) {
      console.error(error);
      notify(
        `Error:${
          error?.message || "Error: Não foi possível se conectar com o servidor"
        } `,
        "error",
        6000,
      );
    } finally {
      setLoading(false);
    }
  };

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return cdns;

    const searchLower = search.toLowerCase();
    return cdns.filter((cdn) =>
      Object.values(cdn).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [cdns, search]);

  const handleOpenCreate = () => {
    setEditingCdn(null);
    setOpenCdnModal(true);
  };

  const handleEditRow = (cdn: Cdn) => {
    setEditingCdn(cdn);
    setOpenCdnModal(true);
  };

  const handleSaveCdn = async (payload: CdnPayload, id?: number) => {
    try {
      setSavingCdn(true);

      if (id) {
        await api.put(`/easyFlow2.0/cdn/${id}`, {
          ...payload,
        });
        notify("CDN atualizado com sucesso", "success");
      } else {
        await api.post(`/easyFlow2.0/cdn`, {
          ...payload,
        });
        notify("CDN criado com sucesso", "success");
      }

      setOpenCdnModal(false);
      setEditingCdn(null);
      await getCdns();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível salvar o CDN",
        "error",
      );
    } finally {
      setSavingCdn(false);
    }
  };

  const handleDeleteRow = (cdn: Cdn) => {
    setSelectedCdn(cdn);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCdn) return;
    try {
      await api.delete(`/easyFlow2.0/cdn/${selectedCdn.id}`);
      notify("CDN removido com sucesso!", "success", 3000);
      await getCdns();
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao remover CDN",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
    } finally {
      setOpenDeleteModal(false);
      setSelectedCdn(null);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      {isMassMode ? (
        <MassCdns
          cdns={cdns}
          search={search}
          onSearchChange={setSearch}
          onBack={() => setIsMassMode(false)}
          onSaved={getCdns}
        />
      ) : (
        <>
          <FlowHeader
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Pesquisar CDNs"
            onEnterEditMode={() => setIsMassMode(true)}
            disableEditMode={loading}
            onAdd={handleOpenCreate}
            onRefresh={getCdns}
            loading={loading}
          />
          <Divider
            flexItem
            sx={{ my: 1, borderColor: theme.palette.divider }}
          />
          <Box sx={styles.boxTable}>
            <FlowTable<Cdn>
              key={`cdns-${tableKey}`}
              id="cdnsFlow"
              rows={rowsForDisplay}
              columns={cdnColumns}
              loadingTable={loading}
              onEditRow={handleEditRow}
              onDeleteRow={handleDeleteRow}
            />
          </Box>
        </>
      )}

      <ModalCdn
        open={openCdnModal}
        loading={savingCdn}
        cdnData={editingCdn}
        onClose={() => {
          setOpenCdnModal(false);
          setEditingCdn(null);
        }}
        onSave={handleSaveCdn}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir o CDN ${
          selectedCdn?.as_name ?? ""
        }?`}
        handleButton={handleConfirmDelete}
        handleCancel={() => setSelectedCdn(null)}
        severity={"error"}
        singleButton={false}
      />
    </Paper>
  );
}
